import { NgTemplateOutlet } from '@angular/common';
import { Component, computed, ElementRef, inject, isDevMode, ChangeDetectionStrategy } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { MatBadge } from '@angular/material/badge';
import { MatDialog } from '@angular/material/dialog';
import { MatIcon } from '@angular/material/icon';
import { MatTooltipModule } from '@angular/material/tooltip';
import { Params, Router, RouterLink, RouterLinkActive, UrlMatcher } from '@angular/router';
import { TranslatePipe } from '@ngx-translate/core';
import { liveQuery } from 'dexie';
import { from } from 'rxjs';
import { Account } from '../../../class/Account';
import { Landmark } from '../../../database/models';
import { AccountStateService } from '../../../services/account-state.service';
import LandmarksService from '../../../services/landmarks.service';
import { LayoutService } from '../../../services/layout.service';
import { MaintenanceService } from '../../../services/maintenance.service';
import { MessageService } from '../../../services/message.service';
import { RoutingService } from '../../../services/routing.service';
import UsersService from '../../../services/users.service';
import { ShowIfTruncatedDirective } from '../../../utils/directives/show-if-truncated.directive';
import { AvatarComponent } from '../../avatar-menu/avatar/avatar.component';
import { SearchDialogComponent } from '../../global-search/search-dialog/search-dialog.component';
import { ChipComponent } from '../../history/chip/chip.component';
import { ALARMS_NOTIFICATIONS } from '../../notifications-tab/notifications.constant';

declare const window: any;

export interface NavigationItem {
  id: Tabs;
  routerLink: RouterLink['routerLink'];
  queryParams?: Params;
  icon: string;
  label: string;
  condition: boolean;
  alert?: number | boolean;
  badge?: number;
  matcher?: UrlMatcher;
}

export type Tabs =
  | 'dashboard'
  | 'map'
  | 'devices'
  | 'device'
  | 'planning'
  | 'missions'
  | 'forms'
  | 'report'
  | 'trip'
  | 'geo'
  | 'management'
  | 'alarm'
  | 'maintenances'
  | 'drivers'
  | 'admin'
  | 'message'
  | 'landmarks'
  | 'mobility'
  | 'demo-tour-step-2';

@Component({
  selector: 'aside-navigation',
  templateUrl: './aside-navigation.component.html',
  styleUrls: ['./aside-navigation.component.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
  imports: [
    ChipComponent,
    MatTooltipModule,
    TranslatePipe,
    RouterLink,
    MatIcon,
    MatBadge,
    NgTemplateOutlet,
    RouterLinkActive,
    AvatarComponent,
    ShowIfTruncatedDirective,
  ],
})
export class AsideNavigationComponent {
  public layoutService = inject(LayoutService);
  private _accountStateService = inject(AccountStateService);
  public $connectedAccount = this._accountStateService.$account;

  private routingService = inject(RoutingService);
  private router = inject(Router);
  private dialog = inject(MatDialog);
  private maintenanceService = inject(MaintenanceService);
  private messageService = inject(MessageService);
  private _elementRef = inject(ElementRef<HTMLElement>);
  private landmarksService = inject(LandmarksService);
  private _usersService = inject(UsersService);

  public appVersion = this.getAppVersion();
  public navigationItems: NavigationItem[] = [];
  public isOpen = false;
  public isLogged = !this.router.routerState.snapshot.url.includes('supervisor');

  public touchMouvement = 0;
  private _resizeObserver: ResizeObserver | null = null;
  public maintenancesDeadline = this.maintenanceService.deadlines;
  public $isMobile = this.routingService.$isMobile;

  public hasLabel = computed(() => this.$isMobile() || this.layoutService.hasOpenedMenu());

  public readonly MATCH_OPTIONS: RouterLinkActive['routerLinkActiveOptions'] = {
    paths: 'subset',
    queryParams: 'ignored',
    matrixParams: 'ignored',
    fragment: 'ignored',
  };

  public $navigationItems = computed(() => {
    return this._getNavigationItem(
      this.$connectedAccount(),
      this.maintenanceService.$deadlines(),
      this.landmarksService.$toValidate(),
      this.messageService.$isNewMessage(),
      this.routingService.$isMobile()
    );
  });

  private $account = toSignal(
    from(liveQuery(async () => this._usersService.getById(this.$connectedAccount()?.uid)))
  );

  public $fullName = computed(() => {
    if (this.$account()) return `${this.$account()?.firstname} ${this.$account()?.lastname}`;

    // When supervisor is connected on an other account
    return `${this.$connectedAccount()?.firstname} ${this.$connectedAccount()?.lastname}`;
  });

  ngAfterViewInit(): void {
    this.layoutService.navigationWidth.set(this._elementRef.nativeElement.offsetWidth);
    this.setupResizeObserver();
  }

  private setupResizeObserver(): void {
    const resizeObserver = new ResizeObserver(entries => {
      const navWidth = entries[0].contentRect.width;
      this.layoutService.navigationWidth.set(navWidth);
    });

    resizeObserver.observe(this._elementRef.nativeElement);
    this._resizeObserver = resizeObserver;
  }

  ngOnDestroy(): void {
    if (this._resizeObserver) {
      this._resizeObserver.disconnect();
    }
  }

  touchStart(event: TouchEvent) {
    this.touchMouvement = event.targetTouches[0].clientX;
  }

  touchMoving(event: TouchEvent) {
    if (this.touchMouvement - event.targetTouches[0].clientX > 55) {
      this.isOpen = false;
    }
  }

  private _getNavigationItem(
    account: Account,
    deadlines: MaintenanceService['deadlines'],
    poiListToValidate: Landmark[],
    isNewMessage: boolean,
    isMobile: boolean
  ): NavigationItem[] {
    return [
      {
        id: 'dashboard',
        routerLink: '/dashboard',
        icon: 'dashboard',
        label: 'TAB.DASHBOARD',
        condition: true,
      },
      {
        id: 'map',
        routerLink: '/map',
        icon: 'map',
        label: 'TAB.MAP',
        condition: true,
      },
      {
        id: 'devices',
        routerLink: '/park',
        queryParams: { view: 'map' },
        icon: 'directions_car',
        label: 'TAB.DEVICE',
        condition: true,
      },
      {
        id: 'demo-tour-step-2',
        routerLink: '/management',
        queryParams: { view: 'map' },
        icon: 'library_books',
        badge: poiListToValidate?.length,
        label: 'TAB.MANAGEMENT',
        condition: true,
      },
      {
        id: 'report',
        routerLink: '/statistics',
        icon: 'query_stats',
        label: 'TAB.STATISTICS',
        condition: true,
      },
      {
        id: 'maintenances',
        routerLink: '/maintenances',
        icon: 'build',
        label: 'TAB.MAINTENANCE',
        badge: deadlines,
        condition: account?.supervisor || account?.maintenance_option,
      },
      {
        id: 'mobility',
        routerLink: '/mobility',
        queryParams: { view: 'map' },
        icon: 'send',
        label: 'TAB.MOBILITY',
        alert: isNewMessage,
        condition: true,
      },
    ];
  }

  public onOpenNavMobile() {
    this.isOpen = !this.isOpen;
  }

  public toggleMenu() {
    this.layoutService.toggleMenu();
  }
  // @HostListener('document:keydown.meta.k', ['$event'])
  public openSearchDialog(e) {
    e.preventDefault();
    this.dialog.open(SearchDialogComponent, {
      width: this.$isMobile() ? '90vw' : '70vw',
      maxWidth: '90vw',
      maxHeight: '80vh',
      position: { top: '10vh' },
    });
  }

  public getAppVersion(): string | null {
    if (isDevMode()) {
      return 'dev';
    }

    const hostname = window.location.hostname;

    if (hostname.includes('.beta.')) {
      return 'beta';
    }

    return null;
  }

  navigateToNotifications() {
    this.router.navigate([ALARMS_NOTIFICATIONS]);
  }
}
